import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useRouterState } from "@tanstack/react-router";
import { LogOut, Menu, X } from "lucide-react";

import { cn } from "@/lib/cn";
import type { NavMenuItem } from "./NavMenu";
import {
  DOC_TABS,
  MASTER_ITEMS,
  TOOLS_ITEMS,
  reportItemsFor,
} from "./nav-items";

interface MobileNavProps {
  isAdmin: boolean;
  /** Optional user email/name shown at the top of the drawer. */
  userLabel?: string;
}

/**
 * Hamburger + slide-in left drawer for viewports below `md:`. The desktop
 * `<NavMenu>` group in `__root.tsx` stays the source of truth at and above
 * `md:`; this component is invisible there (`md:hidden` on both the button
 * and the drawer surface).
 *
 * Touch targets inside the drawer are deliberately ≥ 44 px high
 * (`min-h-[44px]` on each row) so field operators on a phone don't fat-
 * finger through to a neighbouring link.
 */
export function MobileNav({ isAdmin, userLabel }: MobileNavProps) {
  const [open, setOpen] = useState(false);
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  useEffect(() => setOpen(false), [pathname]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open]);

  async function handleSignOut() {
    try {
      await fetch("/api/auth/logout", {
        method: "POST",
        credentials: "include",
      });
    } catch {
      // Even if the call fails (offline, expired session) we still want to
      // boot back to /login so the user lands somewhere consistent.
    }
    window.location.assign("/login");
  }

  return (
    <>
      <button
        type="button"
        aria-label={open ? "Close menu" : "Open menu"}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          "md:hidden inline-flex items-center justify-center rounded-md border border-border bg-white text-neutral-700",
          "size-11",
        )}
      >
        {open ? <X className="size-5" /> : <Menu className="size-5" />}
      </button>

      {/*
        Portal to document.body so the drawer + backdrop escape the
        header's `backdrop-blur` stacking context. Without this, any
        page-level `sticky top-0 z-30` toolbar would paint over the
        drawer even though z-50 looks higher numerically.
      */}
      {open && createPortal(
        <>
          <div
            onClick={() => setOpen(false)}
            aria-hidden="true"
            className="md:hidden fixed inset-0 z-[1200] bg-black/40 backdrop-blur-[1px]"
          />
          <aside
            role="dialog"
            aria-modal="true"
            aria-label="Main navigation"
            className={cn(
              "md:hidden fixed inset-y-0 left-0 z-[1201] w-80 max-w-[85vw]",
              "bg-white shadow-xl border-r border-border",
              "flex flex-col",
            )}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <div className="text-sm">
                <div className="font-semibold text-navy-900">Menu</div>
                {userLabel && (
                  <div className="text-xs text-neutral-500 truncate max-w-[220px]">
                    {userLabel}
                  </div>
                )}
              </div>
              <button
                type="button"
                aria-label="Close menu"
                onClick={() => setOpen(false)}
                className="size-11 inline-flex items-center justify-center rounded-md text-neutral-600 hover:bg-neutral-100"
              >
                <X className="size-5" />
              </button>
            </div>

            <nav className="flex-1 overflow-y-auto py-2">
              {DOC_TABS.map((tab) => (
                <DrawerLink key={tab.to} to={tab.to}>
                  {tab.label}
                </DrawerLink>
              ))}

              <DrawerGroupHeader>Masters</DrawerGroupHeader>
              {MASTER_ITEMS.map((item) => (
                <DrawerItem key={item.to} item={item} />
              ))}

              <DrawerGroupHeader>Reports</DrawerGroupHeader>
              {reportItemsFor(isAdmin).map((item) => (
                <DrawerItem key={item.to} item={item} />
              ))}

              <DrawerGroupHeader>Tools</DrawerGroupHeader>
              {TOOLS_ITEMS.map((item) => (
                <DrawerItem key={item.to} item={item} />
              ))}

              <div className="my-2 border-t border-border" />

              <DrawerLink to="/profile">My profile</DrawerLink>
              {isAdmin && (
                <>
                  <DrawerLink to="/company-profile">Company settings</DrawerLink>
                  <DrawerLink to="/settings/whatsapp">WhatsApp alerts</DrawerLink>
                  <DrawerLink to="/admin/users">Manage users</DrawerLink>
                </>
              )}
            </nav>

            <div className="border-t border-border p-3">
              <button
                type="button"
                onClick={handleSignOut}
                className={cn(
                  "w-full inline-flex items-center justify-center gap-2 rounded-md",
                  "px-3 py-3 min-h-[44px] text-sm font-medium",
                  "text-danger-700 bg-danger-50 hover:bg-danger-100",
                )}
              >
                <LogOut className="size-4" />
                Sign out
              </button>
            </div>
          </aside>
        </>,
        document.body,
      )}
    </>
  );
}

function DrawerLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <Link
      to={to}
      className={cn(
        "block px-4 py-3 min-h-[44px] text-sm text-neutral-700",
        "hover:bg-neutral-50 hover:text-navy-900",
      )}
      activeProps={{ className: "bg-navy-50 text-navy-900 font-medium" }}
      activeOptions={{ exact: true }}
    >
      {children}
    </Link>
  );
}

function DrawerItem({ item }: { item: NavMenuItem }) {
  return (
    <Link
      to={item.to}
      className={cn(
        "block px-4 py-3 min-h-[44px]",
        "hover:bg-neutral-50",
      )}
      activeProps={{ className: "bg-navy-50" }}
    >
      <div className="text-sm font-medium text-neutral-800">{item.label}</div>
      {item.description && (
        <div className="text-xs text-neutral-500 mt-0.5">{item.description}</div>
      )}
    </Link>
  );
}

function DrawerGroupHeader({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-4 pt-4 pb-1 text-[11px] font-semibold uppercase tracking-wider text-neutral-400">
      {children}
    </div>
  );
}
